'use client'

import React, { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { type Post } from '@/payload-types'
import { LinkedinIcon } from '@/components/icons/LinkedinIcon'
import { FacebookIcon } from '@/components/icons/FacebookIcon'
import { XIcon } from '@/components/icons/XIcon'
import { BlueskyIcon } from '@/components/icons/BlueskyIcon'
import { LinkIcon } from '@/components/icons/LinkIcon'
import { LinkButton } from '@/components/ui/LinkButton'
import { Caption } from '@/components/ui/caption'
import { cn } from '@/utilities/ui'

// Simple check icon for copy success state
const CheckIcon = () => (
  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z"
      fill="currentColor"
      fillRule="evenodd"
      clipRule="evenodd"
    />
  </svg>
)

export interface BlogPostSocialSharingProps {
  title?: string
  post: Post
  linksClassName?: string
  className?: string
}

export const BlogPostSocialSharing: React.FC<BlogPostSocialSharingProps> = ({
  post,
  title,
  linksClassName,
  className,
}) => {
  const [copied, setCopied] = useState(false)
  const shareUrl = typeof window !== 'undefined' ? window.location.href : ''

  // Share links
  const linkedInShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
  const xShareUrl = `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title || '')}`
  const blueskyShareUrl = `https://bsky.app/intent/compose?text=${encodeURIComponent(`${post.title} ${shareUrl}`)}`

  // Handle copy link with animation
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      // Reset after animation completes
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className={cn('flex flex-col gap-[20px]', className)}>
      {title && (
        <Caption color="subtle" className="uppercase font-medium">
          {title}
        </Caption>
      )}
      <div className={cn('flex flex-row gap-[20px]', linksClassName)}>
        <LinkButton
          variant="ghost"
          size="clear"
          aria-label="Share on X"
          href={xShareUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <XIcon />
        </LinkButton>
        <LinkButton
          variant="ghost"
          size="clear"
          aria-label="Share on LinkedIn"
          href={linkedInShareUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <LinkedinIcon />
        </LinkButton>
        <LinkButton
          variant="ghost"
          size="clear"
          aria-label="Share on Facebook"
          href={facebookShareUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FacebookIcon />
        </LinkButton>
        <LinkButton
          variant="ghost"
          size="clear"
          aria-label="Share on Bluesky"
          href={blueskyShareUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <BlueskyIcon />
        </LinkButton>
        <Button
          variant="ghost"
          size="clear"
          aria-label={copied ? 'Link copied' : 'Copy link'}
          onClick={handleCopyLink}
        >
          <div className="relative">
            <span
              className={cn(
                'absolute inset-0 flex items-center justify-center transition-all duration-200',
                copied ? 'opacity-100 transform scale-100' : 'opacity-0 transform scale-50',
              )}
            >
              <CheckIcon />
            </span>
            <span
              className={cn(
                'transition-all duration-200',
                copied ? 'opacity-0 transform scale-50' : 'opacity-100 transform scale-100',
              )}
            >
              <LinkIcon />
            </span>
          </div>
        </Button>
      </div>
    </div>
  )
}
